import React, { useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { CircularProgress } from '@mui/material';
import { DisputeAgreement } from 'src/utils/options';
import { nameofFactory } from 'src/utils/nameofFactory';
import acceptIcon from 'src/assets/images/icons/document-text-green.svg';
import rejectIcon from 'src/assets/images/icons/close-circle.svg';
import cancelIcon from 'src/assets/images/icons/back-square.svg';
import { useDisputeOptions, DisputeOptionType } from 'src/use-cases/invoice/useDispute';
import { featureFlags } from 'src/config/featureFlags';
import { DisputeStep, IDisputeFormValues } from './types';
import { DisputeContext } from './DisputeContext';

const nameof = nameofFactory<IDisputeFormValues>();

interface DisputeCardSelectorProps {
  className?: string;
  billedEntityId?: number;
}

export const DisputeCardSelector: React.FC<DisputeCardSelectorProps> = ({ className, billedEntityId }) => {
  const { watch, setValue } = useFormContext<IDisputeFormValues>();
  const selectedAgreementType = watch('agreementType');
  const { setActiveStep } = useContext(DisputeContext);

  // Fetch dispute options from API with billedEntityId
  const { data: apiDisputeOptions, isLoading } = useDisputeOptions(billedEntityId);

  // Map API options to UI format
  const disputeResolutionOptions = useMemo(() => {
    if (!apiDisputeOptions) return [];

    return apiDisputeOptions
      .map((option: DisputeOptionType) => {
        switch (option) {
          case 'ACCEPT_INVOICE':
            return {
              label: featureFlags?.SHOW_INVOICE_APPROVALS ? 'Accept with Credit Note' : 'Accept Invoice',
              description: 'With an expected Credit Note',
              id: DisputeAgreement.ACCEPT_INVOICE_WITH_CREDIT_NOTE,
              icon: '✓',
              color: 'green',
            };
          case 'REJECT_INVOICE':
            return {
              label: 'Reject Invoice',
              description: 'Cancel the invoice',
              id: DisputeAgreement.REJECT_INVOICE,
              icon: '✕',
              color: 'red',
            };
          case 'CANCEL_DISPUTE':
            return {
              label: 'Cancel Dispute',
              description: 'Go back to reviewing audit exceptions',
              id: DisputeAgreement.CANCEL_DISPUTE,
              icon: '↩',
              color: 'orange',
            };
          default:
            return null;
        }
      })
      .filter(Boolean);
  }, [apiDisputeOptions]);

  const handleCardSelect = (agreementType: string) => {
    setValue(nameof('agreementType'), agreementType as any);
    setActiveStep(DisputeStep.Step2_Resolution);
  };

  const handleKeyDown = (event: React.KeyboardEvent, agreementType: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardSelect(agreementType);
    }
  };

  const getCardIcon = (option: (typeof disputeResolutionOptions)[0]) => {
    switch (option.id) {
      case DisputeAgreement.ACCEPT_INVOICE:
        return (
          <div className='w-36 h-36 bg-green-100 rounded-full flex items-center justify-center'>
            <img src={acceptIcon} alt='Accept' className='w-36' />
          </div>
        );
      case DisputeAgreement.ACCEPT_INVOICE_WITH_CREDIT_NOTE:
        return (
          <div className='w-36 h-36 bg-green-100 rounded-full flex items-center justify-center'>
            <img src={acceptIcon} alt='Accept' className='w-36' />
          </div>
        );
      case DisputeAgreement.REJECT_INVOICE:
        return (
          <div className='w-36 h-36 bg-red-100 rounded-full flex items-center justify-center'>
            <img src={rejectIcon} alt='Reject' className='w-36' />
          </div>
        );
      case DisputeAgreement.CANCEL_DISPUTE:
        return (
          <div className='w-36 h-36 bg-orange-100 rounded-full flex items-center justify-center'>
            <img src={cancelIcon} alt='Cancel' className='w-36' />
          </div>
        );
      default:
        return null;
    }
  };

  // Show loading state while fetching or if billedEntityId is not available
  if (!billedEntityId || isLoading) {
    return (
      <div className={clsx('mt-32 xl:mt-48 flex items-center justify-center py-32', className)}>
        <CircularProgress color='primary' />
      </div>
    );
  }

  return (
    <div className={clsx('mt-32 xl:mt-48', className)}>
      <h3 className='text-16 xl:text-20 leading-20 xl:leading-24 mb-4 font-bold'>Select an Option</h3>
      <p className='mb-24 text-blue_grey-90 text-14 xl:text-16 leading-18'>Please choose one:</p>

      <div className='flex flex-col md:flex-row gap-16'>
        {disputeResolutionOptions.map((option) => (
          <div
            key={option.id}
            role='radio'
            tabIndex={0}
            aria-checked={selectedAgreementType === option.id}
            aria-label={`${option.label}: ${option.description}`}
            onClick={() => handleCardSelect(option.id)}
            onKeyDown={(event) => handleKeyDown(event, option.id)}
            className={clsx('flex-1 p-20 border-2 rounded-20 cursor-pointer transition-all duration-200 hover:shadow-md', {
              'border-primary bg-primary/5': selectedAgreementType === option.id,
              'border-neutral-20 bg-white hover:border-neutral-40': selectedAgreementType !== option.id,
            })}
          >
            <div className='flex items-start gap-12'>
              {getCardIcon(option)}
              <div className='flex-1'>
                <h4 className='text-14 xl:text-16 font-bold text-blue_grey-90 mb-4'>{option.label}</h4>
                <p className='text-12 xl:text-14 text-blue_grey-70 leading-16 xl:leading-18'>{option.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
